import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiEnvelope,
  CampaignDetail,
  CampaignDryRun,
  CampaignLane,
  CampaignsOverview,
  SaveCampaignRequest,
} from '@seller-portal/types';
import { api } from '@/services/api';

/** Campaigns through the Merx API (demo sellers can change them; everyone can read). */

const key = (sellerId: string) => ['seller', sellerId, 'campaigns'] as const;

/** `enabled`: off for sellers the campaigns routes refuse (they run for demo sellers only). */
export function useCampaigns(sellerId: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: key(sellerId),
    queryFn: async () =>
      (await api.get<ApiEnvelope<CampaignsOverview>>(`/sellers/${sellerId}/campaigns`)).data.data,
  });
}

/** Polls while a published version is still on its way to the catalog. */
export function useCampaign(sellerId: string, id: string | undefined) {
  return useQuery({
    queryKey: [...key(sellerId), id],
    enabled: Boolean(id),
    queryFn: async () =>
      (await api.get<ApiEnvelope<CampaignDetail>>(`/sellers/${sellerId}/campaigns/${id}`)).data
        .data,
    refetchInterval: (q) =>
      q.state.data?.publicationStatus === 'PUBLISHING' ||
      q.state.data?.publicationStatus === 'ACCEPTED'
        ? 4000
        : false,
  });
}

export function useCreateCampaign(sellerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { lane: CampaignLane; name: string }) =>
      (await api.post<ApiEnvelope<{ id: string }>>(`/sellers/${sellerId}/campaigns`, body)).data
        .data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(sellerId) }),
  });
}

/** Every change answers the fresh campaign; the cache takes it so the page shows it at once. */
function useCampaignChange<TBody>(
  sellerId: string,
  id: string,
  run: (body: TBody) => Promise<CampaignDetail>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (detail) => {
      queryClient.setQueryData([...key(sellerId), id], detail);
      void queryClient.invalidateQueries({ queryKey: key(sellerId), exact: true });
    },
  });
}

export function useSaveCampaign(sellerId: string, id: string) {
  return useCampaignChange(
    sellerId,
    id,
    async (body: SaveCampaignRequest) =>
      (await api.patch<ApiEnvelope<CampaignDetail>>(`/sellers/${sellerId}/campaigns/${id}`, body))
        .data.data
  );
}

/** Points the campaign at the intents picked, or (no ids) every search in its categories. */
export function useChooseIntents(sellerId: string, id: string) {
  return useCampaignChange(
    sellerId,
    id,
    async (nodeIds: string[]) =>
      (
        await api.put<ApiEnvelope<CampaignDetail>>(`/sellers/${sellerId}/campaigns/${id}/intents`, {
          nodeIds,
        })
      ).data.data
  );
}

export function useResolveTarget(sellerId: string, id: string) {
  return useCampaignChange(
    sellerId,
    id,
    async (_: void) =>
      (await api.post<ApiEnvelope<CampaignDetail>>(`/sellers/${sellerId}/campaigns/${id}/target`))
        .data.data
  );
}

export function useCampaignAction(sellerId: string, id: string) {
  return useCampaignChange(
    sellerId,
    id,
    async (action: 'launch' | 'pause' | 'end') =>
      (
        await api.post<ApiEnvelope<CampaignDetail>>(
          `/sellers/${sellerId}/campaigns/${id}/${action}`
        )
      ).data.data
  );
}

export function useDryRun(sellerId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<ApiEnvelope<CampaignDryRun>>(`/sellers/${sellerId}/campaigns/${id}/dry-run`))
        .data.data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...key(sellerId), id] }),
  });
}

export function useDeleteCampaign(sellerId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/sellers/${sellerId}/campaigns/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(sellerId) }),
  });
}
